import { faCommentAltEdit } from '@fortawesome/pro-regular-svg-icons'
import clsx from 'clsx'
import React, { useCallback, useState } from 'react'
import { useQuery } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { Column, SortingRule } from 'react-table'
import isEqual from 'lodash/isEqual'

import experienceService from '../../services/experienceService'
import { Experience } from '../../types/experiences'
import { ExperienceEvaluation } from '../../utils/config'

import EmptyContent from '../EmptyContent'
import ErrorPage from '../ErrorPage'
import Table from '../Table'
import TableSkeleton from '../TableSkeleton'

const Experiences = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const [orderByFilter, setOrderBy] = useState<SortingRule<Experience>[]>([])

  const handleSortByChange = useCallback(
    (orderBy: SortingRule<any>[]) => {
      if (isEqual(orderByFilter, orderBy)) return

      setOrderBy(orderBy)
    },
    [orderByFilter]
  )

  const {
    data: experiences,
    isLoading,
    isError,
  } = useQuery(
    ['userExperiences', id, orderByFilter],
    () =>
      experienceService.getUserExperiences(
        { id },
        {
          ...(orderByFilter && orderByFilter.length > 0
            ? { orderBy: `${orderByFilter[0].id}_${orderByFilter[0].desc ? 'DESC' : 'ASC'}` }
            : null),
        }
      ),
    {
      keepPreviousData: true,
    }
  )

  const columns = React.useMemo<Column<Experience>[]>(
    () => [
      {
        id: 'title',
        Header: 'Title',
        accessor: 'title',
      },
      {
        id: 'placeName',
        Header: 'Location & Area',
        accessor: ({ place }) => (
          <>
            <p className="text-sm font-normal leading-5 text-gray-900">{place.name}</p>
            <p className="text-sm font-normal leading-5 text-gray-500">{place.location.city}</p>
          </>
        ),
      },
      {
        id: 'placeCustomerName',
        Header: 'Customer',
        accessor: ({ author }) => author.user.displayName,
      },
      {
        id: 'evaluation',
        Header: 'Rating',
        accessor: ({ evaluation }) => (
          <span
            className={clsx({
              'tag capitalize': true,
              'positive-tag': evaluation === ExperienceEvaluation.POSITIVE,
              'alert-tag': evaluation === ExperienceEvaluation.NEUTRAL,
              'negative-tag': evaluation === ExperienceEvaluation.NEGATIVE,
            })}
          >
            {evaluation}
          </span>
        ),
      },
      {
        id: 'status',
        Header: 'Status',
        accessor: ({ isValidated, isVerified }) => {
          if (isValidated) {
            return <span className="tag positive-tag">Verified</span>
          }

          if (isVerified) {
            return <span className="tag positive-tag">At Location</span>
          }

          return <span className="tag alert-tag">Unverified</span>
        },
      },
      {
        id: 'createdAt',
        Header: 'Date',
        accessor: ({ createdAt }) => new Date(createdAt).toLocaleDateString('en-GB'),
      },
      {
        Header: ' ',
        disableSortBy: true,
        accessor: ({ id }) => (
          <button
            onClick={() => {
              navigate(`/experiences/${id}`)
            }}
            className="text-link"
          >
            View
          </button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  if (isLoading)
    return <TableSkeleton headers={['Title', 'Location & Area', 'Customer', 'Rating', 'Status', 'Date', ' ']} />

  if (isError || !experiences) {
    return (
      <ErrorPage
        title="Something went wrong!"
        text="We keep track of these errors, but feel free to contact us if refreshing doesn't fix things."
      />
    )
  }

  if (experiences.length === 0) {
    return (
      <div className="mt-32 flex h-full items-center justify-center">
        <EmptyContent
          title="No experiences found."
          description="This user has not written any review."
          icon={faCommentAltEdit}
        />
      </div>
    )
  }

  return (
    <div>
      <Table columns={columns} data={experiences} onSortBy={handleSortByChange} />
    </div>
  )
}

export default Experiences
